import {FC, lazy, Suspense} from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {useAuth} from '../modules/auth'
import {FEATURE_OPTIONS, verifyFeature} from '../modules/feature-toggle/verifyFeature'
import ReportBuilderDetail from './user/reportBuilderDetail/reportBuilderDetail'
import ReportBuilderList from './user/reportBuilderList/reportBuilderList'
import ReportingSelection from './user/reporting/ReportSelection'

const ClientRoutes = () => {
  const DataForms = lazy(() => import('./administration/data-forms/DataForms'))
  const PeriodEditor = lazy(() => import('./administration/period-editor/PeriodEditor'))
  const CollectionList = lazy(() => import('./user/collectionList/CollectionList'))
  const CollectionDetail = lazy(() => import('./user/collectionDetail/CollectionDetail'))
  const CalculationManager = lazy(() => import('./administration/calculations/CalculationManager'))
  const GroupManager = lazy(() => import('./administration/groups/GroupManager'))
  const AdminActions = lazy(() => import('./administration/admin-actions/AdminActions'))

  const {isAdmin} = useAuth()
  const isAdminActionsAvailable = verifyFeature(FEATURE_OPTIONS.ADMIN_ACTIONS)
  const isCalculationManagerAvailable = verifyFeature(FEATURE_OPTIONS.CALCULATION)
  const isGroupManagerAvailable = verifyFeature(FEATURE_OPTIONS.GROUP)
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route
          path='collections/*'
          element={
            <SuspensedView>
              <CollectionList />
            </SuspensedView>
          }
        />

        <Route
          path='collection/:formTypeId/:companyId/:quarterId'
          element={
            <SuspensedView>
              <CollectionDetail />
            </SuspensedView>
          }
        />

        <Route
          path='reporting/*'
          element={
            <SuspensedView>
              <ReportingSelection />
            </SuspensedView>
          }
        />

        <Route
          path='reportBuilder/'
          element={
            <SuspensedView>
              <ReportBuilderList />
            </SuspensedView>
          }
        />

        <Route
          path='reportBuilder/report/:customReportId'
          element={
            <SuspensedView>
              <ReportBuilderDetail />
            </SuspensedView>
          }
        />

        <Route
          path='dataforms/:formType'
          element={
            <SuspensedView>
              <DataForms />
            </SuspensedView>
          }
        />
        <Route
          path='dataforms/:formType/:id'
          element={
            <SuspensedView>
              <DataForms />
            </SuspensedView>
          }
        />
        <Route
          path='periodEditor/*'
          element={
            <SuspensedView>
              <PeriodEditor />
            </SuspensedView>
          }
        />
        <Route
          path='calculationManager/*'
          element={
            isCalculationManagerAvailable ? (
              <SuspensedView>
                <CalculationManager />
              </SuspensedView>
            ) : (
              <Navigate to='/error/404' />
            )
          }
        />
        <Route
          path='groupManager/*'
          element={
            isGroupManagerAvailable ? (
              <SuspensedView>
                <GroupManager />
              </SuspensedView>
            ) : (
              <Navigate to='/error/404' />
            )
          }
        />
        <Route
          path='adminActions/*'
          element={
            isAdmin && isAdminActionsAvailable ? (
              <SuspensedView>
                <AdminActions />
              </SuspensedView>
            ) : (
              <Navigate to='/error/404' />
            )
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {ClientRoutes}
